import * as React from "react";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Breakpoint, Theme, useTheme } from "@mui/material/styles";

const KEYS: Breakpoint[] = ["xs", "sm", "md", "lg", "xl"];

type HiddenProps = React.PropsWithChildren<
  { [K in `${Breakpoint}Up` | `${Breakpoint}Down`]?: boolean } & {
    only?: Breakpoint | Breakpoint[];
    implementation?: "js" | "css";
  }
>;

/** The media queries (without "@media") for widths at which MUI 5's <Hidden> hid its children. */
function hiddenQueries(theme: Theme, props: HiddenProps): string[] {
  const media = (query: string) => query.replace(/^@media\s*/, "");
  const queries: string[] = [];
  KEYS.forEach((key) => {
    if (props[`${key}Up`]) queries.push(media(theme.breakpoints.up(key)));
    // As in MUI 5, "Down" excludes the breakpoint itself (the v5 codemod already shifted the props by one).
    if (props[`${key}Down`] && key !== "xs") queries.push(media(theme.breakpoints.down(key)));
  });
  const only = props.only === undefined ? [] : Array.isArray(props.only) ? props.only : [props.only];
  only.forEach((key) => queries.push(media(theme.breakpoints.only(key))));
  return queries;
}

/**
 * Stand-in for Material UI's <Hidden>, which MUI 7 removed: hides its children at the given breakpoints, with the
 * props and semantics it had in MUI 5.
 */
const Hidden: React.FC<HiddenProps> = (props) => {
  const theme = useTheme();
  const queries = hiddenQueries(theme, props);
  const hidden = useMediaQuery(queries.length ? queries.join(", ") : "not all", { noSsr: true });

  if (props.implementation === "css") {
    const sx = Object.fromEntries(queries.map((query) => [`@media ${query}`, { display: "none" }]));
    return <Box sx={sx}>{props.children}</Box>;
  }

  return hidden ? null : <>{props.children}</>;
};

export default Hidden;
